import {
  ArrowUp,
  CheckSquare,
  Info,
  Plus,
  Square,
} from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useMemo, useState } from "react";

import { View, XStack, YStack } from "tamagui";
import { TKey } from "../../../shared/translations/translationKeys";
import { ModuleState } from "../../../templates/types";
import { useApi } from "../../core/apis";
import { SortableDataItemList } from "../../core/components/SortableList";
import { stringTrimList } from "../../core/utils";
import {
  HelperType,
  type HelpItem,
  HelpItemRequiredType,
} from "../../helper/HelperManager";
import { useHelper } from "../../helper/useHelper";
import { ModuleBlock } from "../../ui/Block";
import { Body } from "../../ui/Body";
import { Box } from "../../ui/Box";
import { CenterContent } from "../../ui/CenterContent";
import { Checkbox } from "../../ui/Checkbox";
import { ModuleEditor } from "../../ui/editor/useModuleEditor";
import type { InfoRowItemProps } from "../../ui/InfoRow";
import { Input } from "../../ui/InputNew";
import { Label } from "../../ui/Label";
import { log } from "../../ui/log";
import { TipsText } from "../../ui/TipsText";
import { ModuleDataType } from "../core/BaseModuleDataModel";
import { ModuleSetupBase } from "../core/ModuleUIRenderer";
import { DoneBox } from "../elements/DoneBox";
import type { PollOptionModelData } from "./data/PollOptionModel";
import { PollDoneSheet } from "./PollDoneSheet";
import type PollModuleModel from "./PollModuleModel";

export const PollModuleStaticUI = observer(
  (_props: { module: PollModuleModel }) => {
    return <Label>Static Content - TODO</Label>;
  },
);

export const PollModuleUI = observer(
  ({ module }: { module: PollModuleModel }) => {
    const { modulesDataApi } = useApi();

    const moduleUid = module.uid;

    React.useEffect(() => {
      const unsub = modulesDataApi.watchDataForModuleUid(moduleUid);
      return () => {
        log.network(
          `Stopped watchDataForModuleUid from moduleUid ${moduleUid}`,
        );
        unsub();
      };
    }, [modulesDataApi, moduleUid]);

    const pollOptions = module.pollOptions;
    const isWaitingForUsers = !!module.waitingForUsers.length;

    const items: HelpItem[] = React.useMemo(
      () => [
        {
          type: HelpItemRequiredType.Required,
          label: "Add a first option",
          isCompleted: () => {
            return pollOptions.length > 0;
          },
        },
        {
          type: HelpItemRequiredType.Optional,
          label: "Add your own vote now",
          isEnabled: () => pollOptions.length > 0,
          isCompleted: () => {
            return module.hasVoted;
          },
        },
        {
          type: HelpItemRequiredType.Required,
          label: "All have voted! Decide on the winning options",
          isEnabled: () => {
            return (
              module.waitingForUsers.length === 0 && module.plan.usersCount > 1
            );
          },
          isCompleted: () => {
            return module.data.state === ModuleState.Done;
          },
        },

        {
          type: HelpItemRequiredType.Tip,
          label: "You can allow multiple answers in settings",
          isEnabled: () => {
            return !module.data.settings.multipleVotes;
          },
          isCompleted: () => {
            return false;
          },
        },
        {
          type: HelpItemRequiredType.Tip,
          label: "You can set a description if needed in settings",
          isEnabled: () => {
            return !module.data.settings.showDescription;
          },
          isCompleted: () => {
            return false;
          },
        },
      ],
      [
        pollOptions,
        module.data.settings.showDescription,
        module.data.settings.multipleVotes,
        module.hasVoted,
        module.data.state,
        module.waitingForUsers,
        module.plan.usersCount,
      ],
    );

    const helper = useHelper(module.uid, HelperType.Poll, items);

    return (
      <YStack gap="$medium">
        {!module.data.title && (
          <Box>
            <XStack gap="$medium">
              <ArrowUp />
              <Body>Set your question above!</Body>
            </XStack>
          </Box>
        )}

        {helper}

        <View>
          {module.data.settings.showDescription && (
            <Box marginBottom="$medium">
              <ModuleEditor module={module} />
            </Box>
          )}

          <Box>
            <TipsText
              text={
                module.isDone
                  ? "Voting is completed"
                  : module.data.settings.multipleVotes
                    ? "Vote for multiple options"
                    : "Vote for one option"
              }
            />

            <View>
              {!!pollOptions.length && (
                <SortableDataItemList items={pollOptions} />
              )}
              {pollOptions.length === 0 && (
                <CenterContent
                  muted
                  title="No options yet"
                  subTitle={"Add some options to the Poll below"}
                  icon={Info}
                />
              )}
            </View>
          </Box>
          {module.isEditable && (
            <Input
              submitOnEnter
              type="padded"
              blurOnSubmit={false}
              borderTopWidth={1}
              borderTopColor={"$divider"}
              attention={pollOptions.length === 0}
              placeholder="Add option..."
              clearOnSubmit
              icon={Plus}
              onSubmitEditing={(e) => {
                const option = e.nativeEvent.text;
                module.addNewData<PollOptionModelData>(
                  ModuleDataType.PollOption,
                  {
                    option,
                  },
                  TKey.ActivityPollOptionAdded,
                  { label: option },
                );
              }}
            />
          )}
        </View>

        {isWaitingForUsers && pollOptions.length ? (
          <Box>
            <Body fontStyle="italic">
              Waiting for answers from {module.waitingForUsers.join(", ")}
            </Body>
          </Box>
        ) : undefined}

        {module.pollOptions.length ? (
          <DoneBox
            ctaLabel={"Decide on final outcome"}
            userIsDone={module.hasVoted}
            waitingForUsers={module.waitingForUsers}
            module={module}
            userReadyUpDescription="Add your own vote and wait for the rest to do the same"
            beforeDoneDescription="When everyone is ready, select the final option(s) to mark the card as completed"
            everyoneReadyDescription="Everyone has voted, select the final option(s) to mark the card as completed"
            doneDescription="Great! You have decided on option(s) together"
          />
        ) : undefined}
        <PollDoneSheet module={module} />
      </YStack>
    );
  },
);

export const PollModuleBlockUI = observer(
  ({ module }: { module: PollModuleModel }) => {
    const infoRowItems = useMemo<InfoRowItemProps[]>(
      () => [
        {
          text: `${module.data.snapshot?.numVoted ?? 0} voted`,
          icon: CheckSquare,
        },
        {
          text: `${module.data.snapshot?.numNotVoted ?? 0} remaining`,
          icon: Square,
        },
      ],
      [module.data.snapshot],
    );

    return (
      <ModuleBlock
        module={module}
        infoRowItems={infoRowItems}
        doneText={stringTrimList(module.data.snapshot.selectedOptions)}
      />
    );
  },
);

export const PollModuleSetupUI = observer(
  ({ module }: { module: PollModuleModel }) => {
    const [hasMultipleVotes, setHasMultipleVotes] = useState(true);

    return (
      <YStack gap="$larger">
        <ModuleSetupBase
          module={module}
          title="Question"
          titlePlaceholder="Set question..."
          description="Setup by defining a question and set an icon"
        />
        <XStack
          onPress={() => {
            setHasMultipleVotes(!hasMultipleVotes);
            module.updateSettings({ multipleVotes: !hasMultipleVotes }, true);
          }}
          cursor="pointer"
          gap="$medium"
        >
          <Checkbox
            checked={hasMultipleVotes}
            onCheckedChange={(checked: boolean) => {
              setHasMultipleVotes(checked);
              module.updateSettings({ multipleVotes: checked }, true);
            }}
          />
          <Body>Multiple choices allowed</Body>
        </XStack>

        <XStack
          onPress={() => {
            module.updateSettings(
              { showDescription: !module.data.settings.showDescription },
              true,
            );
          }}
          cursor="pointer"
          gap="$medium"
        >
          <Checkbox
            checked={module.data.settings.showDescription}
            onCheckedChange={(_checked: boolean) => {
              module.updateSettings(
                { showDescription: !module.data.settings.showDescription },
                true,
              );
            }}
          />
          <Body>Show description</Body>
        </XStack>
      </YStack>
    );
  },
);
